import React, { CSSProperties, ReactElement, ReactNode } from 'react'
import styled from 'styled-components'
import { onlyString } from '../helpers/dataTransformer'
import colors from '../lib/colors'
import theme, { ZIndex } from '../lib/theme'

const Styles = styled.div`
  color: ${colors.black};
  .styled-input {
    /* layout */
    min-height: ${theme.fieldHeight};
    min-width: 20px;
    padding: 4px 5px;
    height: inherit;
    width: 100%;
    /* border */
    border: 1px solid;
    border-radius: 4px;
    box-sizing: border-box;
    border-color: ${colors.darkGray};
    /* style */
    font-family: inherit;
    color: ${colors.black};
    font-size: 1rem;
    /* transition */
    transition: border-color ease-in-out 0.3s;
    ::placeholder {
      color: ${colors.darkerGray};
    }
    :hover {
      border-color: ${colors.darkerGray};
    }
    :focus {
      transition: none;
      outline: none;
      border-color: ${colors.lightBlue};
    }
    /* state */
    &.valid {
      border-color: ${colors.green};
      color: ${colors.green};
    }
    &.error {
      border-color: ${colors.red};
      color: ${colors.red};
      background-color: ${colors.lightestRed};
    }
    &.with-widget {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    /* disabled input still readable, e.g. in Gift Card */
    :disabled {
      background-color: ${colors.white};
      border-color: ${colors.lightGray};
    }
  }
  /* whole component disabled, e.g. in Web Shop */
  &.field-disabled {
    transition: 0.2s;
    opacity: 0.65;
    pointer-events: none;
  }
  .input-label-group {
    display: flex;
    justify-content: flex-end;
  }
  .input-label {
    font-size: 0.9rem;
    font-weight: bold;
    margin-left: 2px;
    margin-bottom: 3px;
    margin-right: auto;
  }
  .input-subtitle {
    font-size: 0.9rem;
    margin-left: 2px;
    margin-top: 3px;
  }
  .input-icon {
    svg {
      stroke: ${colors.darkGray};
      height: 16px;
      width: 16px;
    }
    position: absolute;
    top: 0;
    bottom: 0;
    right: 7px;
    pointer-events: none;
    display: flex;
    align-items: center;
  }
  .widget-wrapper {
    display: flex;
    align-items: center;
  }
  .widget {
    /* layout */
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: ${theme.fieldHeight};
    min-width: 30px;
    margin-right: -1px;
    padding: 0 5px;
    /* border */
    border: 1px solid;
    border-radius: 4px 0 0 4px;
    box-sizing: border-box;
    border-color: ${colors.darkGray};
    /* style */
    font-family: ${theme.fontFamily};
    color: ${colors.black};
    background-color: ${colors.lightGray};
    &.clickable {
      /* state */
      cursor: pointer;
      /* transition */
      transition: background-color ease-in-out 0.2s;
      :hover {
        background-color: ${colors.gray};
      }
      :active {
        transition: none;
        background-color: ${colors.gray};
      }
    }
    &.disabled {
      transition: 0.2s;
      opacity: 0.65;
      pointer-events: none;
    }
  }
  .input-long {
    min-width: 250px;
  }
  .input-short {
    width: auto;
  }
  .dropdown-cover {
    z-index: ${ZIndex.AboveContent - 1};
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
  }
  .dropdown,
  .react-datepicker,
  .select__menu {
    /* layout */
    position: absolute;
    width: fit-content;
    min-width: 180px;
    margin-top: 5px;
    z-index: ${ZIndex.AboveContent};
    box-shadow: 2px 3px 7px ${colors.shadow} !important;
    /* border */
    border: 1px solid;
    border-radius: 4px !important;
    box-sizing: border-box;
    border-color: ${colors.darkerGray};
    /* transition */
    transition: border-color ease-in-out 0.3s;
    :hover {
      border-color: ${colors.lightBlue};
    }
    &.with-items {
      /* scroll */
      ${theme.scrollbar}
      overflow-y: auto;
      max-height: 200px;
      /* style */
      font-family: ${theme.fontFamily};
      color: ${colors.black};
      background-color: ${colors.white};
    }
  }
  .dropdown-item,
  .select__option {
    cursor: pointer;
    padding: 5px;
    white-space: nowrap;
    :first-child {
      margin-top: 3px;
    }
    :last-child {
      margin-bottom: 3px;
    }
    :hover {
      background-color: ${colors.lightGray};
    }
    &.ignoreHover:hover {
      cursor: default;
      background-color: inherit;
    }
  }
  &.field-large {
    input {
      min-height: ${theme.largeFieldHeight};
      padding: 0 8px;
      font-size: 1.15rem;
    }
    .input-label {
      font-size: 1rem;
    }
    .widget {
      min-height: ${theme.largeFieldHeight};
      padding: 0 7px;
    }
  }
`
const RightLabel = styled.div`
  font-size: 0.9rem;
  margin-right: 8px;
  display: flex;
  gap: 8px;
  color: ${(props): string => (props.theme.error ? colors.red : 'inherit')};
`
export enum FieldSize {
  Large = 'large',
  Standard = 'standard',
}
export interface IFieldStylesProps {
  children: ReactNode
  className?: string
  style?: CSSProperties
  label?: string | null
  maxChars?: string | null
  maxLines?: string | null
  hideMaxChars?: boolean
  subtitle?: string | null
  disabled?: boolean
  fieldSize?: FieldSize
  onClick?: () => void
  error?: string
}
function FieldStyles({
  children,
  className,
  style,
  label,
  maxChars,
  hideMaxChars,
  maxLines,
  subtitle,
  disabled,
  fieldSize,
  onClick,
  error,
}: IFieldStylesProps): ReactElement {
  const sizeClass = `field-${fieldSize}`
  const disabledClass = disabled ? 'field-disabled' : ''

  const showMaxChar = maxChars && !hideMaxChars && <div>{maxChars}</div>

  return (
    <Styles
      className={`${className} ${disabledClass} ${sizeClass}`}
      style={style}
      onClick={onClick}
    >
      <div className="input-label-group">
        {label && (
          <label className="input-label" htmlFor={onlyString(label)}>
            {label}
          </label>
        )}
        <RightLabel theme={{ error }}>
          {error || (
            <React.Fragment>
              <div>{maxLines && <div>{maxLines}</div>}</div>
              <div>{showMaxChar}</div>
            </React.Fragment>
          )}
        </RightLabel>
      </div>
      {children}
      {subtitle && <div className="input-subtitle">{subtitle}</div>}
    </Styles>
  )
}
export default FieldStyles
